import { ModelItem } from '@/types';
import moment from 'moment';

// Visually distinct tag color palette — hues spread evenly across the color wheel
// Using Tailwind 200-level backgrounds + 800-level text for readability
const TAG_PALETTE: Array<{ bg: string; text: string }> = [
  { bg: "#bfdbfe", text: "#1e40af" }, // blue      (~215°)
  { bg: "#a7f3d0", text: "#065f46" }, // emerald   (~160°)
  { bg: "#ddd6fe", text: "#5b21b6" }, // violet    (~265°)
  { bg: "#fde68a", text: "#92400e" }, // amber     (~45°)
  { bg: "#fecdd3", text: "#9f1239" }, // rose      (~355°)
  { bg: "#a5f3fc", text: "#164e63" }, // cyan      (~185°)
  { bg: "#d9f99d", text: "#365314" }, // lime      (~90°)
  { bg: "#f0abfc", text: "#701a75" }, // fuchsia   (~295°)
];

export function getTagColor(key: string): { bg: string; text: string } {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return TAG_PALETTE[hash % TAG_PALETTE.length];
}

interface ErrorDetail {
  type: string;
  loc: (string | number)[];
  msg: string;
  input: string;
  ctx: {
    lt: number;
  };
}

interface ResponseBody {
  detail: ErrorDetail[];
}

interface Request {
  method: string;
  url: string;
  query: {
    limit: number;
  };
  errors: {
    '404': string;
    '422': string;
  };
}

interface ApiError {
  url: string;
  status: number;
  statusText: string;
  body: ResponseBody;
  request: Request;
  name: string;
}

export const TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export type AlertType = 'error' | 'warning' | 'info' | 'success';
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error: string;
}

export const alertMsg = (alertTxt: string, alertType: AlertType = 'error') => {
  const patchEvent = new CustomEvent('showAlertMsg', {
    detail: {
      alertTxt,
      alertType,
    },
  });
  window.dispatchEvent(patchEvent);
};

export const formatTime = (timeStr: string | number) => {
  if (!timeStr) {
    return '-';
  }
  const timeString = String(timeStr);
  return moment.utc(timeString).local().format(TIME_FORMAT);
};

export const handleErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    const errorString = JSON.stringify(error);
    const errorObj: ApiError = JSON.parse(errorString);
    const showMessage = errorObj?.body?.detail ?? '';
    if (typeof showMessage === 'string') {
      alertMsg(`${error.message}: ${showMessage}`);
    } else {
      alertMsg(
        `${error.message}: ${errorObj?.body?.detail?.[0]?.msg ?? 'Unknown error'}`,
      );
    }
  }
};

export const formatFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes < 0) {
    return 'Invalid size';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let unitIndex = 0;
  let size = sizeInBytes;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)} ${units[unitIndex]}`;
};

// Split S3 Key String into bucket name and prefix
const getPosition = (string: string, subString: string, index: number) => {
  return string.split(subString, index).join(subString).length;
};

interface BucketAndPrefix {
  bucket: string;
  prefix: string;
}

export const splitStringToBucketAndPrefix = (
  s3KeyStr: string,
): BucketAndPrefix => {
  const tmpBucketObj: BucketAndPrefix = {
    bucket: '',
    prefix: '',
  };
  const prefixIndex = getPosition(s3KeyStr, '/', 3);
  let tmpLogBucket = '';
  let tmpLogPath = '';
  const p1 = s3KeyStr.slice(0, prefixIndex);
  const p2 = s3KeyStr.slice(prefixIndex + 1);
  const s3BucketArr = p1.split('//');
  if (s3BucketArr && s3BucketArr.length > 1) {
    tmpLogBucket = s3BucketArr[1];
  }
  tmpLogPath = p2;
  tmpBucketObj.bucket = tmpLogBucket;
  tmpBucketObj.prefix = tmpLogPath;
  return tmpBucketObj;
};

export const bucketNameIsValid = (bucketName: string): boolean => {
  if (!bucketName) {
    return false;
  }

  const REG1 = new RegExp(`${'^[a-z\\d.-]*$'}`).test(bucketName);
  const REG2 = new RegExp(`${'^[a-z\\d]'}`).test(bucketName);
  const REG3 = bucketName.endsWith('-') === false;
  const REG4 = new RegExp(`${'\\.\\.+'}`).test(bucketName) === false;
  const REG5 = new RegExp(`${'-+\\.$'}`).test(bucketName) === false;
  const REG6 =
    new RegExp(
      `${'^(?:(?:^|\\.)(?:2(?:5[0-5]|[0-4]\\d)|1?\\d?\\d)){4}$'}`,
    ).test(bucketName) === false;
  const REG7 = bucketName.length >= 3 && bucketName.length <= 63;

  return REG1 && REG2 && REG3 && REG4 && REG5 && REG6 && REG7;
};

export const getDirPrefixByPrefixStr = (prefix: string) => {
  if (prefix && prefix.indexOf('/') >= 0) {
    const slashPos = prefix.lastIndexOf('/');
    prefix = prefix.slice(0, slashPos + 1);
  }
  return prefix;
};

export const buildS3Link = (
  region: string,
  bucketName: string,
  prefix?: string,
): string => {
  if (region?.startsWith('cn')) {
    if (prefix) {
      const resPrefix = getDirPrefixByPrefixStr(prefix);
      if (resPrefix.endsWith('/')) {
        return `https://console.amazonaws.cn/s3/buckets/${bucketName}?region=${region}&prefix=${resPrefix}`;
      }
    }
    return `https://console.amazonaws.cn/s3/buckets/${bucketName}`;
  }
  if (prefix) {
    const resPrefix = getDirPrefixByPrefixStr(prefix);
    if (resPrefix.endsWith('/')) {
      return `https://s3.console.aws.amazon.com/s3/buckets/${bucketName}?region=${region}&prefix=${resPrefix}`;
    }
  }
  return `https://s3.console.aws.amazon.com/s3/buckets/${bucketName}`;
};

export const buildS3LinkByString = (
  region: string,
  s3String: string,
): string => {
  const { bucket, prefix } = splitStringToBucketAndPrefix(s3String);
  return buildS3Link(region, bucket, prefix);
};

export const getOIDCRedirectURL = (
  cloudFrontUrl: string,
  customDomain?: string,
) => {
  if (customDomain) {
    return customDomain?.toLocaleLowerCase()?.startsWith('localhost')
      ? `http://${customDomain}/signin`
      : `https://${customDomain}/signin`;
  } else {
    return cloudFrontUrl?.toLocaleLowerCase()?.startsWith('localhost')
      ? `http://${cloudFrontUrl}/signin`
      : `https://${cloudFrontUrl}/signin`;
  }
};

export const getOIDCPopupRedirectURL = (
  cloudFrontUrl: string,
  customDomain?: string,
) => {
  if (customDomain) {
    return customDomain?.toLocaleLowerCase()?.startsWith('localhost')
      ? `http://${customDomain}/popup-callback`
      : `https://${customDomain}/popup-callback`;
  } else {
    return cloudFrontUrl?.toLocaleLowerCase()?.startsWith('localhost')
      ? `http://${cloudFrontUrl}/popup-callback`
      : `https://${cloudFrontUrl}/popup-callback`;
  }
};

export const transformModelData = (data: ModelItem[]) => {
  // 通过 reduce 对数据进行分组
  const groupedData = data.reduce((groups, item) => {
    const groupLabel = item.model_family;

    // 如果该分组不存在，则创建
    if (!groups[groupLabel]) {
      groups[groupLabel] = [];
    }

    // 将选项添加到对应分组
    groups[groupLabel].push({
      label: item.model_id,
      value: item.model_id,
    });

    return groups;
  }, {});

  // 将分组数据转换为指定格式
  return (
    Object.keys(groupedData)
      // `OPENAI_COMPATIBLE` used to appear as a bare selectable entry -- picking it
      // asked for a model id, a base URL and a secret name by hand, on every screen
      // that offered it and again per context. Model configurations do that once and
      // are reusable, so the family is no longer offered as a choice here; a custom
      // endpoint is added on /models and shows up in the "Custom" group.
      //
      // Contexts already configured this way still read correctly: their model_id is
      // resolved by findModelOption, which falls back to the stored id when no
      // option matches.
      .filter((group) => group !== "OPENAI_COMPATIBLE")
      .map((group) => ({
        label: group,
        options: groupedData[group],
      }))
  );
};
